// Step 11 — ShareButton: tap → compose card → upload → invoke share sheet.
//
// Used in three places (Decision: minimal entry set):
//   1. ColoringPage completion overlay
//   2. PackScene colored cell detail panel
//   3. InventoryPage colored item card (icon variant)
//
// Caller provides `getHero` so this component stays decoupled from where the
// pixel data lives — ColoringPage hands its live canvas, scene/inventory hand
// a presigned R2 GET URL we then `loadCorsImage`.

import { useState } from 'react'
import { Loader2, Share2 } from 'lucide-react'
import {
  composeShareCard,
  ShareCardCompositionError,
  type ShareCardInput,
} from '../lib/share-card-canvas.ts'
import { sharesApi, putShareCardR2 } from '../scene/sharesApi.ts'
import type { PackId } from '@skena/schema'

interface ShareButtonProps {
  artworkId: string
  /** Returns the hero image element (CORS-clean) at compose time. */
  getHero: () => Promise<HTMLImageElement>
  itemNameZh: string
  itemNameEn: string
  scriptureRef: string
  descriptionZh: string
  displayName: string
  churchName: string | null
  packId: PackId
  /** UI variant. `primary` = full button, `icon` = compact (InventoryPage). */
  variant?: 'primary' | 'icon'
  /**
   * True for bonus / panorama artworks — flips the share card to render the
   * "✦ 全景大獎" capstone badge. Step 13 (final-reward).
   */
  isCapstone?: boolean
  /** Optional callback fired after the share sheet is invoked / clipboard fallback runs. */
  onShared?: () => void
}

const PACK_BUTTON: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-primary)] text-white',
  nativity: 'bg-[var(--color-nativity-primary)] text-white',
}

export function ShareButton({
  artworkId,
  getHero,
  itemNameZh,
  itemNameEn,
  scriptureRef,
  descriptionZh,
  displayName,
  churchName,
  packId,
  variant = 'primary',
  isCapstone = false,
  onShared,
}: ShareButtonProps) {
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  const onTap = async () => {
    if (busy) return
    setBusy(true)
    setToast('準備分享卡片…')
    try {
      // 1. Open share row + sign card PUT URL.
      const created = await sharesApi.create(artworkId)

      // 2. Load hero (CORS-clean) and compose share card PNG.
      const heroImg = await getHero()
      const cardInput: ShareCardInput = {
        heroImg,
        itemNameZh,
        itemNameEn,
        scriptureRef,
        descriptionZh,
        displayName,
        date: new Date(),
        churchName,
        isCapstone,
      }
      const blob = await composeShareCard(cardInput)

      // 3. PUT to R2.
      setToast('上傳分享卡片…')
      await putShareCardR2(created.cardPutUrl, blob)

      // 4. Always copy the URL to clipboard, then invoke the share sheet.
      //
      // The native share sheet (esp. desktop) doesn't always offer a "copy link"
      // receiver, so users dogfooded a flow of share-to-other-app-just-to-copy.
      // Writing clipboard unconditionally turns the share sheet into a bonus
      // instead of the only path to the URL.
      let clipboardWritten = false
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(created.shareUrl)
          clipboardWritten = true
        } catch {
          // Browsers may reject after multiple awaits drain the user-gesture
          // window. Silent — toast falls back to showing the URL.
        }
      }

      const shareData: ShareData = {
        url: created.shareUrl,
        text: created.shareTextZh,
      }
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
          await navigator.share(shareData)
        } catch (err) {
          // User cancel or share unsupported — clipboard already has the URL.
          if (!(err instanceof DOMException && err.name === 'AbortError')) {
            console.warn('[share] navigator.share failed', err)
          }
        }
      }

      setToast(clipboardWritten ? '已複製分享連結' : created.shareUrl)
      window.setTimeout(() => setToast(null), clipboardWritten ? 2400 : 5000)
      onShared?.()
    } catch (err) {
      console.error('[share] failed', err)
      const reason = err instanceof ShareCardCompositionError ? err.reason : 'unknown'
      setToast(reason === 'canvas-tainted' ? '圖片載入失敗，請重試' : '分享卡片準備失敗，請重試')
      window.setTimeout(() => setToast(null), 2400)
    } finally {
      setBusy(false)
    }
  }

  if (variant === 'icon') {
    return (
      <>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void onTap()
          }}
          disabled={busy}
          aria-label={`分享 ${itemNameZh}`}
          aria-busy={busy}
          className="rounded-full bg-white/90 p-2 text-[var(--color-fg)] shadow-sm transition-shadow hover:shadow-md disabled:cursor-wait disabled:opacity-70"
        >
          {busy ? (
            <Loader2 size={18} aria-hidden className="animate-spin" />
          ) : (
            <Share2 size={18} aria-hidden />
          )}
        </button>
        <ShareToast toast={toast} />
      </>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => void onTap()}
        disabled={busy}
        aria-busy={busy}
        className={`inline-flex items-center gap-2 rounded-[var(--radius-pill)] px-5 py-2 text-sm font-semibold disabled:cursor-wait disabled:opacity-70 ${PACK_BUTTON[packId]}`}
      >
        {busy ? (
          <>
            <Loader2 size={16} aria-hidden className="animate-spin" /> 準備中…
          </>
        ) : (
          <>❦ 分享給家人</>
        )}
      </button>
      <ShareToast toast={toast} />
    </>
  )
}

function ShareToast({ toast }: { toast: string | null }) {
  if (!toast) return null
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed top-6 left-1/2 z-50 -translate-x-1/2 rounded-[var(--radius-pill)] bg-[var(--color-fg)] px-4 py-2 text-sm text-white shadow"
    >
      {toast}
    </div>
  )
}
